// The mark. TWO renders sharing one gradient, each owning a size range.
//
// 🔴 This file used to build the mark entirely from markup, and a test pinned
// that ("the app builds its mark from markup, not from the PNGs"). That rule
// was correct for a geometric disc-and-numeral mark and it is now RETIRED
// DELIBERATELY, not abandoned: round 3 of the design brought in the hand-drawn
// D2D stamp from the Discord server banner, and a hand-drawn signature cannot
// be built from markup at all. See the rewritten test in
// src/test/design-system.test.js for the full reasoning.
//
// The handoff's size ladder is the whole ruling, and it is why BOTH renders
// still exist:
//
//   Hero stamp   >=96px    the stamp, all four layers   landing hero, OG, splash
//   Nav stamp    32-64px   the stamp, art + keyline     top bar, app icons
//   Disc         <32px     gradient disc, 2 knocked out favicon, tab bar, prose
//
// "Below 32px the stamp turns to mush [...] That is why the disc still exists,
// and why it isn't optional." So the stamp is the signature at hero and nav,
// and the disc keeps the small end — here, the 16px footer lockup.
//
// Two things the design session got wrong itself, and warned about:
//
//   1. The numeral goes in an INNER element. Its font-size would otherwise
//      shrink the disc's own em box — that bug rendered the hero disc at 36px
//      instead of 54px.
//   2. The disc's horizon line comes off at or below 24px. It is 1px at every
//      size, so below that it cuts through the numeral instead of sitting under
//      the sun. The only surface still using the disc is the 16px footer, which
//      is below that threshold — so the disc render carries no horizon at all
//      and there is no prop to turn one on. The hero's horizon line is a
//      different element: it runs BEHIND the stamp, per the hero lockup spec.
//
// The numeral is knocked out — it is --d2d-bg showing through, not white. On a
// light surface this would need the background painted behind it; the site is
// dark-first single-theme in the SHIPPED css, so that case does not exist yet.
// (Light mode was accepted 2026-09-12 and is not implemented; when it lands,
// revisit this — see docs/design-briefs/portal/HANDOFF-admin-estate-board.md §6.2.)

const RENDERS = {
  hero: { modifier: 'd2d-stamp--hero', src: '/icons/stamp-hero.png', horizon: true },
  nav: { modifier: 'd2d-stamp--nav', src: '/icons/stamp-nav.png', horizon: false },
}

// One accessible name for the whole lockup, every render. The design
// prototype puts alt="D A Y 2 D A Y" on the stamp AND ships a visible
// "DAY2DAY" wordmark beside it, which a screen reader reads twice. Here the
// stamp and the wordmark are both decorative and the name is carried once, by
// the visually-hidden span — the pattern the disc render already used.
const NAME = 'D A Y 2 D A Y'

export default function Lockup({ size = 'nav' }) {
  const render = RENDERS[size]

  // The disc: markup, so the numeral stays real Archivo at every size.
  if (!render) {
    return (
      <span className="d2d-lockup d2d-lockup--footer">
        <span className="d2d-sr-only">{NAME}</span>
        <span aria-hidden="true">DAY</span>
        <span className="d2d-disc d2d-gradient" aria-hidden="true">
          <span className="d2d-disc__numeral">2</span>
        </span>
        <span aria-hidden="true">DAY</span>
      </span>
    )
  }

  // The stamp: artwork, so it must be an image.
  return (
    <span className={`d2d-stamp ${render.modifier}`}>
      <span className="d2d-sr-only">{NAME}</span>
      <span className="d2d-stamp__art">
        <img className="d2d-stamp__img" src={render.src} alt="" aria-hidden="true" />
        {render.horizon ? <span className="d2d-stamp__horizon" aria-hidden="true" /> : null}
      </span>
      {/* Nav only: a 1px divider between the stamp and the typeset wordmark. */}
      <span className="d2d-stamp__rule" aria-hidden="true" />
      {/* Type, never an image. "The stamp is the signature; the typeset
          wordmark tells a stranger how to read it. Don't drop it — without it
          the hero is a graffiti tag with no name."

          🔴 P6-221 — THE NAV DROPS IT IN TWO BANDS, AND THE HERO NEVER DOES.
          The instruction above is about the HERO and it stands. The nav had been
          "dropping" it anyway, in the worst possible way: `.d2d-topbar__lockup`
          clips, so the mark rendered "DAY2DA'" at 320 and "DAY2" at 768 on every
          route, with no ellipsis and nothing in any gate able to see it.

          Jordan ruled on 2026-09-08 that the nav can drop the wordmark, and then
          narrowed it: only where the mark is genuinely clipped, keeping it on
          phones. Measured, that is 336-and-under and 768-to-1023 — so the nav
          render is the signature alone there, and the whole lockup everywhere
          else. ⚠️ Present, absent, present as the viewport grows; the derivation
          for both numbers is in app.css above `.d2d-stamp--nav`'s media query,
          and it is not a typo. Do not "correct" it to one breakpoint here or
          there.

          ⚠️ It stays in the MARKUP at every width, and CSS decides whether it is
          painted. Two reasons, and neither is style: src/test/design-system.test.js
          asserts the nav render still contains it, and — the load-bearing one —
          the accessible name is the .d2d-sr-only span above, not this. This span
          is aria-hidden, so hiding it costs a screen reader nothing, and that is
          only true while the name lives somewhere else. The viewport gate asserts
          both halves at every width rather than trusting this paragraph. */}
      <span className="d2d-stamp__wordmark" aria-hidden="true">
        DAY2DAY
      </span>
    </span>
  )
}
